import DataTable from 'components/DataTable'
import FilterSelector from 'components/DropdownSelectors/FilterSelector'
import Translation from 'components/Translation'
import { useAuth } from 'context/AuthContext'
import { useEffect, useState } from 'react'
import { classNames } from 'utils/dom-helpers'
import { patchApiRequest, putApiRequest, useApiRequest, useFetch } from 'utils/fetch-helper'
import { timetoHHMM, toCurrency } from 'utils/string-localization-helper'
import { FetchMethods } from 'utils/types'
import { Appointment } from '../types'
import Pagination, { IPagination } from 'components/Pagination/Pagination'
import { MAX_DATE } from 'types/client'
import { useTenantContext } from 'context/TenantContext'
import { useAppContext } from 'context/AppContext'
import { getTranslation } from 'utils/language-helper'
import { denormalizeService } from 'services/service'
import { CalendarSlotItem } from 'types/calendar'
import { Order, OrderType } from 'types/order'
import { ServiceApiTypeEnum } from 'types/service'
import { AttendeeItem, AttendeeListItemApiResponse } from 'types/attendee'
import { denormalizeOrder } from 'services/order'
import { LocationItem } from 'types/location'
import React from 'react'
import { Tooltip } from '@progress/kendo-react-tooltip'
import { useBookingService } from 'services/booking'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { StaffUser } from 'types/staff'
import { sendBookingNotification } from 'services/notifications'
import { GlobalDialogType } from 'components/Modals/GlobalDialog'

function EmptyAppointments(props: any) {
    return (
        <div className="text-center py-10">
            <div className="rounded-full bg-gray-100 w-20 h-20 inline-block leading-loose text-center align-middle">
                <i className="text-gray-400 feather feather-calendar text-4xl leading-loose" />
            </div>
            <div className="text-gray-400 block mt-4 mx-auto">
                <Translation content_key="client_currently_no_appointments" />
            </div>
            <button
                className="bg-primary rounded-lg border-gray-200 btn-gap text-sm border shadow-xl mt-6 text-white"
                onClick={props.handleAdd}
            >
                <Translation content_key="add_appointment" />
            </button>
        </div>
    )
}

function RenderAppointments(props: any) {
    const { translations, locale } = useAppContext()
    const { coupons } = useTenantContext()
    const tooltip_ref = React.useRef<Tooltip>(null)
    const appointments: Appointment[] = props.appointments
    const rows: HTMLTableRowElement[] = (appointments || []).map(
        (
            {
                id,
                date,
                time,
                title,
                staff,
                location,
                status,
                staff_photo,
                order,
                signed_in_at
            }: Appointment,
            idx
        ) => {
            const coupon = order?.couponCode
                ? coupons.find((c) => c.code == order.couponCode)
                : undefined
            const has_responses = props.order_ids_with_responses && props.order_ids_with_responses.includes(order.id)
            return (
                <tr
                    key={id}
                    className={classNames(
                        idx % 2 ? 'bg-gray-50' : '',
                        'hover:bg-blue-300 hover:bg-opacity-30'
                    )}
                >
                    <td
                        className="px-3 py-3 align-top whitespace-nowrap"
                    >
                        <span className="block font-bold text-base" id={id}>
                            {new Date(date).toLocaleString(locale, {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                            })}
                        </span>
                        <span className="block">{timetoHHMM(time)}</span>
                    </td>
                    <td
                        className="px-3 py-3 align-top w-full max-w-[30%] whitespace-normal"
                    >
                        <span className="block font-bold text-base">{title}</span>
                        <span className="flex items-center">
                            <img
                                className="rounded-full w-4 h-4 mr-2"
                                src={staff_photo}
                                alt="Staff Photo"
                            />
                            {staff}
                        </span>
                        <span className="block">{location}</span>
                    </td>
                    <td
                        className="px-3 py-3 align-top whitespace-nowrap flex flex-col"
                    >
                        <div>
                            <i
                                className={classNames(
                                    ' mr-2',
                                    status == 'Unpaid' ? 'feather-x text-[#bcbec0]' : 'text-green-500 feather-check'
                                )}
                                aria-hidden="true"
                            />
                            <span className="mr-2 text-base">{status}</span>
                        </div>
                        {status == 'Unpaid' && (
                            <button className="text-xxs uppercase text-orange-500">
                                <Translation
                                    content_key='clients.pay_now'
                                />
                            </button>
                        )}
                    </td>
                    <td
                        className="px-3 py-3 align-top whitespace-nowrap cursor-default"
                    >
                        {coupon
                            ?
                            <>
                                <span className='text-primary-light font-medium text-base'
                                    title={coupon.code}
                                    onMouseOver={event => tooltip_ref.current && tooltip_ref.current.handleMouseOver(event)}
                                    onMouseOut={event => tooltip_ref.current && tooltip_ref.current.handleMouseOut(event)}
                                >{order.couponCode}</span>
                                <Tooltip ref={tooltip_ref} anchorElement="target" position="top"
                                    content={props =>
                                        <div>
                                            <div className='font-bold'>{coupon.code}:</div>
                                            <div>{coupon.description}</div>
                                        </div>
                                    }
                                />
                            </>

                            :
                            <span>-</span>
                        }
                    </td>
                    <td
                        className="px-3 py-3 align-top text-center whitespace-nowrap"
                    >
                        <i
                            className={classNames(
                                ' mr-2',
                                signed_in_at ? 'text-green-500 feather-check' : 'feather-x text-[#bcbec0]'
                            )}
                            aria-hidden="true"
                            title={signed_in_at ? `${getTranslation("calendar.checked_in_at", translations)} ${new Date(signed_in_at).toLocaleString(locale, {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric'
                            })}` : getTranslation("calendar.not_checked_in", translations)}
                        />
                    </td>
                    <td className="px-3 py-3 align-top whitespace-nowrap">
                        <div className='flex gap-1'>
                            <button
                                className='rounded-md bg-primary-light bg-opacity-10 w-7 h-7 text-secondary-blue disabled:cursor-not-allowed disabled:bg-[#BCBEC01A]/10 disabled:text-black/20'
                                title={getTranslation('forms.view_responses', translations)}
                                disabled={!has_responses}
                                onClick={() => {
                                    props.handleShowResponses && props.handleShowResponses(order.id)
                                }}
                            >
                                <i className="feather-clipboard text-sm mt-1" />
                            </button>
                            <button
                                className='rounded-md bg-red-200 bg-opacity-40 w-7 h-7'
                                title={getTranslation('common.resend_title', translations)}
                                onClick={props.handleResend(idx)}
                            >
                                <i className="feather-mail text-sm mt-1 text-red-500" />
                            </button>
                            <button
                                className='rounded-md bg-primary-lighter bg-opacity-50 w-7 h-7'
                                title={getTranslation('edit', translations)}
                                onClick={props.handleEdit(idx)}
                            >
                                <i className="feather-edit text-sm mt-1 text-gray-900" />
                            </button>
                        </div>

                        {/* <DropDownButton
                            className="bg-primary-lighter bg-opacity-50"
                            iconClass="feather-more-horizontal"
                            popupSettings={{
                                anchorAlign: {
                                    horizontal: 'right',
                                    vertical: 'bottom',
                                },
                                popupAlign: {
                                    horizontal: 'right',
                                    vertical: 'top',
                                },
                            }}
                            items={[
                                {
                                    text: getTranslation('resched', translations),
                                    iconClass: 'feather-corner-up-left',
                                },
                                {
                                    text: getTranslation('cancel', translations),
                                    iconClass: 'feather-x',
                                    className: 'text-orange-500',
                                },
                            ]}
                            itemRender={(props: {
                                item: any
                                itemIndex: number
                            }) => {
                                return (
                                    <div className="p-2">
                                        <span
                                            className={`mr-2 ${props.item.iconClass}`}
                                            role="presentation"
                                        />
                                        <span
                                            className={`${props.item.className}`}
                                        >
                                            {props.item.text}
                                        </span>
                                    </div>
                                )
                            }}
                        /> */}
                    </td>
                </tr>
            ) as unknown as HTMLTableRowElement
        }
    )

    return (
        <div className="flex flex-col">
            <div className="align-middle inline-block min-w-full">
                <div className="flex justify-between">
                    <div className="flex gap-3">
                        {/* TODO: Add Date Filter here */}
                        <FilterSelector
                            items={[]}
                            is_filtered={props.is_filtered}
                            setIsFiltered={props.setIsFiltered}
                            is_appointment_filter_enabled={true}
                        />
                    </div>
                    <button
                        onClick={props.handleAdd}
                        className="flex items-center bg-primary-lighter text-primary text-sm btn-gap rounded-lg mb-3"
                    >
                        <i className="feather-plus text-sm mr-2" />
                        <Translation content_key="add_appointment" />
                    </button>
                </div>
                <div className="overflow-hidden mt-4">
                    <DataTable
                        rows={rows}
                        columns={[
                            {
                                label: 'Date',
                                classNames:
                                    'px-3 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                            },
                            {
                                label: 'Appointment',
                                classNames:
                                    'px-3 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                            },
                            {
                                label: getTranslation("clients.payment", translations),
                                classNames:
                                    'px-3 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                            },
                            {
                                label: getTranslation("clients.coupon_applied", translations),
                                classNames:
                                    'px-3 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                            },
                            {
                                label: getTranslation("clients.checked_in", translations),
                                classNames:
                                    'px-3 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                            },
                            {
                                label: 'Actions',
                                classNames:
                                    'px-3 py-3 text-left text-xs text-gray-500 uppercase tracking-wider',
                            },
                        ]}
                    />
                    <Pagination {...props.paginationProps} />
                </div>
            </div>
        </div>
    )
}

export function Appointments() {
    const { GlobalDialog } = useAuth()
    const { getApiRequest } = useApiRequest()
    const { setGlobalError } = useAppErrorApiContext()
    const { getGroupClassAvailabilityBookings } = useBookingService()
    const { setGlobalLoading } = useAppContext()
    const { locations, all_staff, tenant } = useTenantContext()
    const { CreateClientModal } = useAuth()
    const { attributes, setAttributes } = CreateClientModal
    const [appointments, setAppointments] = useState<Appointment[]>([])
    const [pageSize, setPageSize] = useState(10)
    const [currentPage, setCurrentPage] = useState(0)
    const currentDate = new Date().toISOString().slice(0, 10)
    const from = currentDate
    const to = MAX_DATE
    const [is_filtered, setIsFiltered] = useState(true)
    const [order_ids_with_responses, setOrderIdsWithResponses] = useState<string[]>([])

    // Appointments List
    const {
        data: client_appointments_api_response,
        doFetch: getClientAppointments,
    } = useFetch(
        `/v1/bookings/appointments-by-client-id?clientId=${attributes?.id
        }&size=${pageSize}&page=${currentPage}&from=${is_filtered ? from : ''
        }&to=${is_filtered ? to : ''}`,
        FetchMethods.GET,
        !!attributes?.id
    )

    const paginationProps: IPagination = {
        totalPages: client_appointments_api_response?.totalPages,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        totalRecords: client_appointments_api_response?.totalElements,
    }

    useEffect(() => {
        if (client_appointments_api_response.content && locations) {
            const current = new Date()
            current.setHours(0, 0, 0, 0)
            let fetched_appointments =
                client_appointments_api_response.content
                    .map((a: any) => {
                        const staffId =
                            a.groupClassSettingDto?.staffId ||
                            a.bookingDto?.appointmentDto?.staffId
                        const locationId =
                            a.groupClassSettingDto?.locationId ||
                            a.bookingDto?.appointmentDto?.locationId

                        let selected_staff = all_staff.find(
                            (s) => s.id == staffId
                        )
                        let selected_location = locations.find(
                            (l: LocationItem) =>
                                l.id == locationId
                        )

                        const service = denormalizeService(a?.serviceDto)

                        let payment = a.orderDto?.payments[0]
                        let amount = payment?.amount || 0
                        return {
                            id: a.bookingDto.id,
                            date:
                                a.groupClassAvailabilityDto?.date ||
                                a.bookingDto.appointmentDto?.date,
                            time:
                                a.groupClassAvailabilityDto?.startTime ||
                                a.bookingDto.appointmentDto?.startTime,
                            start_time:
                                a.groupClassAvailabilityDto?.startTime ||
                                a.bookingDto.appointmentDto?.startTime,
                            end_time:
                                a.groupClassAvailabilityDto?.endTime ||
                                a.bookingDto.appointmentDto?.endTime,
                            title: a.serviceDto.name,
                            staff_object: selected_staff,
                            staff:
                                selected_staff?.user?.first_name +
                                ' ' +
                                selected_staff?.user?.last_name,
                            staff_photo: selected_staff?.user?.image_url || '/images/staff_icon.svg',
                            location_object: selected_location,
                            location: selected_location?.name,
                            status: a.orderDto.fullyPaid
                                ? 'Paid'
                                : 'Unpaid',
                            amount: toCurrency(amount),
                            imageUrl: a.serviceDto.imageUrl,
                            group_class_id: a?.groupClassAvailabilityDto?.groupClassId,
                            availability_id: a?.groupClassAvailabilityDto?.id,
                            setting_id: a?.groupClassSettingDto?.id,
                            appointment_id: a?.bookingDto?.appointmentDto?.id,
                            location_space_id: a?.bookingDto?.appointmentDto?.locationSpaceId ||
                                a?.groupClassSettingDto?.locationSpaceId,
                            location_space_name: a?.bookingDto?.appointmentDto?.locationSpaceName ||
                                selected_location?.location_spaces?.find((space) =>
                                    space.id == a?.groupClassSettingDto?.locationSpaceId)?.name
                            ,
                            service_object: service,
                            attendees: a?.bookingDto?.appointmentDto?.attendees,
                            order: a.orderDto,
                            fully_paid: a.orderDto.fullyPaid,
                            notes: a?.bookingDto?.notes,
                            signed_in_at: a?.bookingDto?.signedInAt,
                            group_class_name: a?.groupClassDto?.name
                        } as Appointment
                    })
            setAppointments(fetched_appointments)
        }
    }, [client_appointments_api_response, locations])

    function handleAdd() {
        window.open(
            `${window.location.origin}/book/?t=${tenant?.id}`,
            '_blank',
            'noopener,noreferrer'
        )
    }

    function handleEdit(idx: number) {
        return async () => {
            const selected_appointment = appointments[idx]
            const service = selected_appointment?.service_object
            const start = new Date([selected_appointment?.date, selected_appointment?.start_time.split(" ")[0]].join('T'))
            const end = new Date(
                start.getTime() + (service?.duration || 0) * 60000
            )
            let attendees: AttendeeItem[] = []
            let main_attendee_idx = 0
            if (service?.service_type == ServiceApiTypeEnum.APPOINTMENT) {
                attendees = [{
                    id: (selected_appointment?.attendees as string[])?.[0],
                    booking_id: selected_appointment.id,
                    user: {
                        id: (selected_appointment?.attendees as string[])?.[0],
                        email: (attributes?.email as string),
                        firstName: (attributes?.first_name as string),
                        lastName: (attributes?.last_name as string),
                        phone: (attributes?.phone as string),
                    },
                    order: denormalizeOrder(selected_appointment?.order),
                    paid: selected_appointment?.fully_paid,
                    notes: selected_appointment?.notes,
                    signed_in_at: selected_appointment?.signed_in_at
                }] as AttendeeItem[]
            } else if (selected_appointment?.availability_id) {
                let attendees_xhr = await getApiRequest(`/v1/bookings/attendees/?groupClassAvailabilityId=${selected_appointment.availability_id}`)
                const bookings_output =
                    await getGroupClassAvailabilityBookings(
                        selected_appointment.availability_id
                    )
                attendees = attendees_xhr.attendees.map(
                    ({
                        attendee,
                        bookingId,
                        order: order_api_output,
                    }: AttendeeListItemApiResponse, idx: number) => {
                        if (attributes?.user_id == attendee.user.id) {
                            main_attendee_idx = idx
                        }
                        const booking = bookings_output.bookings.find((b: Record<string, string>) => b.booking_id == bookingId)
                        const order: Order = denormalizeOrder(order_api_output)
                        return {
                            ...attendee,
                            booking_id: bookingId,
                            order,
                            signed_in_at: booking?.signed_in_at,
                            notes: booking?.notes
                        }
                    }
                )
            }
            const data = {
                id: selected_appointment.id,
                appointment_id: selected_appointment?.appointment_id || undefined,
                availability_id: selected_appointment?.availability_id || undefined,
                slots: {
                    available: (service?.max_participants || 0) - attendees.length,
                    taken: attendees.length,
                    max: service?.max_participants,
                },
                service_id: service?.id,
                title: [service?.name, selected_appointment?.location_object?.name].join(' @ '),
                service_name: service?.name,
                location_name: selected_appointment?.location_object?.name,
                location_id: selected_appointment?.location_object?.id,
                price: service?.price,
                setting_id: selected_appointment?.setting_id,
                start,
                startTime: start,
                end,
                primary_hex: service?.primary_color,
                staff_id: selected_appointment?.staff_object?.id,
                staff_name: selected_appointment?.staff,
                location_space_id: selected_appointment?.location_space_id,
                location_space_name: selected_appointment?.location_space_name,
                staff_photo: selected_appointment?.staff_photo,
                service_type: service?.service_type,
                group_class_name: selected_appointment?.group_class_name,
                order: selected_appointment?.order
            } as CalendarSlotItem

            setAttributes({
                ...attributes,
                show: true,
                anim_css: ['bg-opacity-70', ' '],
                data: data,
                staff: (selected_appointment?.staff_object?.user != undefined) ? {
                    first_name: selected_appointment.staff_object.user.first_name,
                    last_name: selected_appointment.staff_object.user.last_name,
                    imageUrl: selected_appointment.staff_object.user.image_url //TODO: standardize variable names
                } as StaffUser : {},
                attendees,
                booking_form: {
                    availability_id: data.availability_id,
                    appointment_id: data.appointment_id,
                    booking_id: data.booking_id,
                    location_id: data.location_id,
                    order_type: OrderType.SERVICE,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    service_id: data.service_id,
                    price: data.price,
                    amount: data.price,
                    end: data.end,
                    start: data.start,
                },
                main_attendee_idx: main_attendee_idx,
                onUpdate: getClientAppointments
            })
        }
    }

    function handleCancel(idx: number) {
        return async () => {
            try {
                setGlobalLoading(true)
                let selected_appointment = appointments[idx]
                let api = await patchApiRequest(
                    `/v1/bookings/cancel/${selected_appointment.id}`
                )
                if (api.ok) {
                    getClientAppointments()
                } else {
                    throw new Error(api.message)
                }
            } catch (err) {
                setGlobalError(err)
            } finally {
                setGlobalLoading(false)
            }
        }
    }

    function handleResend(idx: number) {
        return async () => {
            try {
                setGlobalLoading(true)
                const selected_appointment = appointments[idx]
                const res = await sendBookingNotification(selected_appointment?.id, attributes?.id as string)
                if (res?.ok) {
                    GlobalDialog.setAttributes({
                        title: 'common.resend_success_title',
                        message: attributes?.email as string,
                        className: 'w-1/2 overflow-hidden',
                        type: GlobalDialogType.SUCCESS,
                    })
                    GlobalDialog.open()
                }
                else {
                    throw res
                }
            } catch (e) {
                setGlobalError(e)
            } finally {
                setGlobalLoading(false)
            }
        }
    }

    useEffect(() => {
        fetchOrderIds()
    }, [appointments])

    async function fetchOrderIds() {
        if (attributes?.id && appointments?.length > 0) {
            try {
                let res = await getApiRequest(`/v1/intake-forms/responses/get-order-ids-by-client-id?clientId=${attributes.id as string}&orderIds=${appointments.map((attendee) => attendee.order?.id).join(',')}`)
                if (res?.length > 0) {
                    setOrderIdsWithResponses(res)
                }
            } catch (error) {
                setGlobalError(error)
            }
        } else {
            setOrderIdsWithResponses([])
        }
    }

    return (
        <div>
            {appointments.length == 0 && !is_filtered ? (
                <EmptyAppointments handleAdd={handleAdd} />
            ) : (
                <RenderAppointments
                    appointments={appointments}
                    client_appointments_api_response={
                        client_appointments_api_response
                    }
                    handleAdd={handleAdd}
                    handleEdit={handleEdit}
                    handleCancel={handleCancel}
                    handleResend={handleResend}
                    is_filtered={is_filtered}
                    setIsFiltered={setIsFiltered}
                    paginationProps={paginationProps}
                    order_ids_with_responses={order_ids_with_responses}
                    handleShowResponses={(order_id: string) => {
                        setAttributes({
                            ...attributes,
                            response_order_id: order_id,
                            show_responses: true
                        })
                    }}
                />
            )}
        </div>
    )
}

export default Appointments
